import React, { HTMLAttributes } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { Box, Typography, IconButton, SxProps, Theme } from '@mui/material';
import { ExpandChevron } from 'components/general';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { useIntl } from 'react-intl';

export interface ChecklistTreeNodeHeaderProps {
  displayNumber: string;
  title: string;
  expanded: boolean;
  onExpandToggle: () => void;
  onDelete?: () => void;
  dragHandleProps?: HTMLAttributes<HTMLElement>;
  titleFontWeight?: number;
  titleColor?: string;
  titleFontStyle?: 'normal' | 'italic';
  containerSx?: SxProps<Theme>;
}

const HEADER_CONTAINER_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: '100%',
};

const NUMBER_SX: SxProps<Theme> = {
  fontWeight: 500,
  minWidth: 32,
  color: 'text.primary',
};

const HEADER_ICON_BTN_SX: SxProps<Theme> = {
  color: 'text.secondary',
  flexShrink: 0,
  '&:hover': {
    color: 'text.secondary',
  },
  '&.Mui-focusVisible': {
    color: 'text.secondary',
    bgcolor: 'action.hover',
  },
};

const HEADER_DELETE_ICON_BTN_SX: SxProps<Theme> = {
  color: 'text.primary',
  flexShrink: 0,
  '&:hover': {
    color: 'text.primary',
  },
  '&.Mui-focusVisible': {
    color: 'text.primary',
    bgcolor: 'action.hover',
  },
};

/** {@link HTMLAttributes} may include `color`, which conflicts with MUI {@link IconButtonProps}. */
function iconButtonSafeHtmlAttrs(
  props: HTMLAttributes<HTMLElement> | undefined
): Omit<HTMLAttributes<HTMLElement>, 'color'> {
  if (!props) {
    return {};
  }
  const { color: _omitColor, ...rest } = props;
  return rest;
}

export function ChecklistTreeNodeHeader({
  displayNumber,
  title,
  expanded,
  onExpandToggle,
  onDelete,
  dragHandleProps,
  titleFontWeight = 500,
  titleColor = 'text.primary',
  titleFontStyle = 'normal',
  containerSx,
}: ChecklistTreeNodeHeaderProps): React.ReactElement {
  const intl = useIntl();

  const combinedSx: SxProps<Theme> = containerSx 
    ? [HEADER_CONTAINER_SX, ...(Array.isArray(containerSx) ? containerSx : [containerSx])]
    : HEADER_CONTAINER_SX;

  return (
    <Box sx={combinedSx}>
      <IconButton
        size="small"
        sx={HEADER_ICON_BTN_SX}
        onClick={(event) => {
          event.stopPropagation();
        }}
        {...iconButtonSafeHtmlAttrs(dragHandleProps)}
      >
        <DragHandleIcon fontSize="small" />
      </IconButton>
      <Typography variant="body1" sx={NUMBER_SX}>
        {displayNumber}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: titleFontWeight,
          color: titleColor,
          fontStyle: titleFontStyle,
        }}
      >
        {title}
      </Typography>
      {onDelete && (
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          sx={HEADER_DELETE_ICON_BTN_SX}
          title={formatTranslation(intl, CommonTranslationKeys.delete)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton
        size="small"
        sx={HEADER_ICON_BTN_SX}
        onClick={(event) => {
          event.stopPropagation();
          onExpandToggle();
        }}
        aria-expanded={expanded}
      >
        <ExpandChevron expanded={expanded} />
      </IconButton>
    </Box>
  );
}

export const MemoChecklistTreeNodeHeader = React.memo(ChecklistTreeNodeHeader);
